import { delay } from '@/utils/delay'
import { AlternateLanguage } from '@prismicio/client'
import cookie from 'js-cookie'
import { HeaderDocument } from '../../prismicio-types'
import { Lang, LANG_COOKIE, SHORT_LANG } from '@/const'
import styles from './Header.module.css'
import { asHTML } from '@prismicio/client'
import linkResolver from '../linkResolver'

export class Header {
	private readonly node: HTMLElement
	private readonly items: HTMLElement[]
	private readonly links: HTMLAnchorElement[]
	private readonly button: HTMLElement
	private readonly langLinks: HTMLAnchorElement[]
	private isOpen = false

	constructor(node: HTMLElement) {
		this.onClick = this.onClick.bind(this)
		this.onLangClick = this.onLangClick.bind(this)
		this.node = node
		this.items = Array.from(this.node.querySelectorAll(`.${styles.Item}`) as NodeListOf<HTMLElement>)
		this.links = Array.from(this.node.querySelectorAll(`.${styles.Item} a`) as NodeListOf<HTMLAnchorElement>)
		this.button = this.node.querySelector(`.${styles.Button}`) as HTMLElement
		this.button.addEventListener('click', this.onClick)
		this.langLinks = Array.from(this.node.querySelectorAll('a[data-lang]') as NodeListOf<HTMLAnchorElement>)
		this.langLinks.forEach((link) => link.addEventListener('click', this.onLangClick))
	}

	async onClick() {
		this.isOpen = !this.isOpen

		if (this.isOpen) {
			this.node.classList.remove(styles.IsVisible)
			this.node.classList.add(styles.IsOpen)
			await delay(250)
			this.node.classList.add(styles.IsVisible)
		} else {
			this.node.classList.remove(styles.IsOpen)
			this.node.classList.add(styles.IsVisible)
		}
	}

	onLangClick(event: MouseEvent) {
		const lang = (event.currentTarget as HTMLAnchorElement).dataset.lang
		if (lang) {
			cookie.set(LANG_COOKIE, lang, { expires: 365, path: '/', sameSite: 'lax' })
		}
	}

	hide() {
		this.isOpen = false
		this.node.classList.remove(styles.IsVisible)
		this.node.classList.remove(styles.IsOpen)
	}

	show(route: string) {
		const active = this.links.findIndex((link) => {
			const { pathname } = new URL(link.href)
			return pathname === route
		})

		this.items.forEach((item, index) =>
			index === active ? item.classList.add(styles.IsActive) : item.classList.remove(styles.IsActive)
		)

		this.node.classList.add(styles.IsVisible)
	}

	dispose() {
		this.button.removeEventListener('click', this.onClick)
		this.langLinks.forEach((link) => link.removeEventListener('click', this.onLangClick))
	}

	static render(props: HeaderDocument, alternate?: AlternateLanguage) {
		const {
			lang,
			data: { items }
		} = props

		return /* HTML */ `
			<header class="${styles.Main}">
				<div class="${styles.Logo}"><a href="/${SHORT_LANG[lang as Lang]}">ZiKD</a></div>
				<div class="${styles.Menu}">
					${
						items &&
						items
							.map(({ link }, index) => {
								const delay = `transition-delay: ${100 + index * 100}ms`
								return /* HTML */ `<div class="${styles.Item}">
									<div class="${styles.Inner}" style="${delay}">
										${asHTML(link, { linkResolver })}
									</div>
								</div>`
							})
							.join('')
					}
					${
						alternate
							? /* HTML */ `
									<div class="${styles.Item}">
										<div
											class="${styles.Inner}"
											style="transition-delay: ${100 + items.length * 100}ms"
										>
											<a
												data-lang="${SHORT_LANG[alternate.lang as Lang]}"
												href="${linkResolver({
													uid: alternate.uid,
													type: alternate.type,
													lang: alternate.lang as Lang
												})}"
												>${SHORT_LANG[alternate.lang as Lang].toUpperCase()}</a
											>
										</div>
									</div>
								`
							: ''
					}
				</div>
				<button class="${styles.Button}"><span></span><span></span></button>
			</header>
		`
	}
}
